import './newrelic'
import React from 'react'
import ReactDOM from 'react-dom/client'
import { ApolloProvider } from '@apollo/client'
import * as Sentry from '@sentry/react'
import * as serviceWorker from './serviceWorker'

import App from './App'
import initApollo from './api/client'
import { APP_VERSION } from 'lib/app-version'
import './index.scss'
import 'styles/globals.scss'
import 'styles/main.scss'
import 'bootstrap/dist/css/bootstrap-grid.min.css'

type SentryEventLike = {
  message?: string
  logentry?: {
    message?: string
  }
  exception?: {
    values?: Array<{
      value?: string
      stacktrace?: {
        frames?: Array<{
          filename?: string
        }>
      }
    }>
  }
}

type SentryHintLike = {
  originalException?: unknown
}

const ignoredSentryErrorFragments = [
  'A listener indicated an asynchronous response by returning true, but the message channel closed before a response was received',
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
]

const getOriginalExceptionMessage = (originalException: unknown): string => {
  if (!originalException) return ''
  if (originalException instanceof Error) return originalException.message
  if (typeof originalException === 'string') return originalException

  try {
    return JSON.stringify(originalException) ?? ''
  } catch {
    return String(originalException)
  }
}

const isBrowserExtensionEvent = (event: SentryEventLike): boolean =>
  event.exception?.values?.some((exception) =>
    exception.stacktrace?.frames?.some((frame) =>
      frame.filename?.startsWith('chrome-extension://') ||
      frame.filename?.startsWith('moz-extension://') ||
      frame.filename?.startsWith('safari-web-extension://')
    )
  ) ?? false

const shouldDropSentryEvent = (event: SentryEventLike, hint: SentryHintLike): boolean => {
  const eventText = [
    event.message,
    event.logentry?.message,
    event.exception?.values?.map((exception) => exception.value).join('\n'),
    getOriginalExceptionMessage(hint.originalException),
  ]
    .filter(Boolean)
    .join('\n')

  return isBrowserExtensionEvent(event) ||
    ignoredSentryErrorFragments.some((fragment) => eventText.includes(fragment))
}

const sentryIntegrations = [
  Sentry.browserTracingIntegration(),
  Sentry.breadcrumbsIntegration(),
  Sentry.consoleLoggingIntegration(),
  Sentry.rewriteFramesIntegration(),
]

if (process.env.REACT_APP_SENTRY_CAPTURE_CONSOLE === 'true') {
  sentryIntegrations.push(
    Sentry.captureConsoleIntegration({
      levels: ['warn', 'error'],
    })
  )
}

Sentry.init({
  dsn: "https://d799ea515dd090b95635ec2aabd0fe72@o4510351607988224.ingest.de.sentry.io/4510391137665104",
  environment: process.env.REACT_APP_ENV,
  release: APP_VERSION,
  integrations: sentryIntegrations,
  tracesSampleRate: Number(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE ?? 0.1),
  sendDefaultPii: true,
  beforeSend: (event, hint) => {
    if (shouldDropSentryEvent(event, hint)) return null

    return event
  },
})

const client = initApollo()

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)
root.render(
  <ApolloProvider client={client}>
    <App />
  </ApolloProvider>
)

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
